import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Category } from '@Types/category.types';
import { useGlobalization } from '@Translations/useGlobalization';
import { stripCategorySuffix } from '@Utils/stripCategorySuffix';

// The picture the numbers can't give on their own: one donut that shows the
// SHAPE of the model. Every factor (component) is a slice, sized by its share
// of the overall score — category weight x the factor's weight within its
// category. So you see, at a glance, all the factors and how much each one
// actually counts. One blue family throughout, darkest → lightest, so it reads
// as one calm palette.

interface Props {
	categories: Category[];
	axisLabel: string;
	size?: number;
	// Card heading — defaults to "Summary" (the LoF/CoF setup panels); the Risk
	// Setup preview passes "LoF" / "CoF" so two donuts don't both say Summary.
	title?: string;
}

interface Seg {
	name: string;
	category: string;
	within: number; // weight within its category (the % on the slider)
	value: number; // share of the model (out of 100), before rounding
	color: string;
	// Set on a category that combines by highest score: it can't be split by
	// component (its score is the worst factor), so it's one slice — this is the
	// number of factors it takes the max over.
	highestOf?: number;
	// Grey filler slice for the portion of the model nothing is weighted to
	// yet — shown so an incomplete model reads as incomplete, not "100%".
	unallocated?: boolean;
}

// A single component's share of its category's component-weight budget: 100
// if it's the only one (nothing to split with), otherwise its own raw
// weighting — deliberately NOT rescaled to fill 100% when the category's
// components don't actually add up to 100. Rescaling would draw an
// under/over-allocated category as if it were complete, and disagree with
// the % shown on its own slider; any shortfall shows up model-wide instead,
// via the "Unallocated" slice below.
const componentShareWithinCategory = (
	componentWeighting: number,
	selectedCount: number,
): number => {
	if (selectedCount === 1) return 100;
	return componentWeighting;
};

// A calm, cool categorical palette for the slices — distinct enough to read as
// a chart, but all in the blue→indigo→violet family (no green or warm hues) so
// it still feels part of the system.
const DONUT_PALETTE = [
	'#3b8fd4', // azure (matches the accent)
	'#232f7a', // navy
	'#9b5de0', // light violet
	'#4aa6ef', // sky
	'#5b3fb0', // deep purple
	'#c77fd8', // orchid
];

export const ModelDonut = ({
	categories,
	axisLabel,
	size = 172,
	title,
}: Props) => {
	const { t } = useGlobalization();
	const heading = title ?? t('Summary');

	const weighted = categories.filter(
		cat => !cat.useThisOnly && cat.weighting > 0,
	);

	const segs: Seg[] = [];
	let factorCount = 0;
	weighted.forEach(cat => {
		const selected = (cat.components ?? []).filter(c => c.select);
		if (selected.length === 0) return;
		factorCount += selected.length;
		const catName = stripCategorySuffix(cat.name);
		// Highest-score category: its score is the worst of its factors, so it
		// can't be split — represent it as one slice sized by the category weight.
		if (cat.useHighestScore) {
			segs.push({
				name: catName,
				category: catName,
				within: 100,
				value: cat.weighting,
				color: '',
				highestOf: selected.length,
			});
			return;
		}
		selected.forEach(comp => {
			const within = componentShareWithinCategory(
				comp.weighting || 0,
				selected.length,
			);
			segs.push({
				name: comp.name,
				category: catName,
				within: Math.round(within),
				value: (cat.weighting / 100) * within,
				color: '',
			});
		});
	});

	segs.forEach((s, idx) => {
		s.color = DONUT_PALETTE[idx % DONUT_PALETTE.length];
	});

	// The model's true share is out of 100, not out of whatever the segments
	// happen to add up to — normalising to the (possibly incomplete or
	// over-allocated) sum would make an unbalanced model look complete.
	const modelTotal = segs.reduce((s, x) => s + x.value, 0);
	if (segs.length > 0 && Math.round(modelTotal) < 100) {
		segs.push({
			name: t('Unallocated'),
			category: '',
			within: 0,
			value: 100 - modelTotal,
			color: '#e7ebf0',
			unallocated: true,
		});
	}

	const stroke = 24;
	const r = (size - stroke) / 2;
	const cx = size / 2;
	const circ = 2 * Math.PI * r;
	// A hair of background ring shows between segments as a divider.
	const gapPx = segs.length > 1 ? 2 : 0;

	const shell = (children: React.ReactNode) => (
		<Box
			sx={{
				background: '#fff',
				border: '1px solid #eceef2',
				borderRadius: '6px',
				p: 2.5,
				mb: 2,
				boxShadow: '0 1px 2px rgba(16,24,40,.04)',
			}}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 0.75,
					mb: 0.25,
				}}>
				<Typography
					sx={{ fontSize: 16, fontWeight: 700, color: '#0d1520' }}>
					{heading}
				</Typography>
				<Tooltip
					arrow
					title={t(
						'Each slice is a factor’s share of the whole {{axis}} score — its weight within its category (the % on its slider) times that category’s weight. So a factor set to, say, 49% inside a category that is 57% of {{axis}} is really ~28% of the whole model.',
						{ axis: axisLabel },
					)}>
					<InfoOutlined
						sx={{
							fontSize: 15,
							color: '#b3bac4',
							cursor: 'default',
						}}
					/>
				</Tooltip>
			</Box>
			<Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
				{t('How your {{axis}} score is composed', { axis: axisLabel })}
			</Typography>
			{children}
		</Box>
	);

	if (segs.length === 0) {
		return shell(
			<Typography sx={{ fontSize: 13, color: '#7c879a' }}>
				{t(
					'Add factors with weights to see how your model is composed.',
				)}
			</Typography>,
		);
	}

	let acc = 0; // cumulative fraction 0..1
	const arcs = segs.map(seg => {
		// Clamp to whatever's left of the ring — an over-allocated model
		// (categories summing above 100%) must not wrap a later segment back
		// over an earlier one. The balance banner already flags "over"; this
		// just stops the donut from drawing a false "complete" ring on top of it.
		const frac = Math.max(0, Math.min(seg.value / 100, 1 - acc));
		const start = acc;
		acc += frac;
		const len = Math.max(0, frac * circ - gapPx);
		return {
			...seg,
			frac,
			// stroke-dashoffset rotates the segment to its start position
			dashArray: `${len} ${circ - len}`,
			dashOffset: -(start * circ),
		};
	});

	return shell(
		<>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					position: 'relative',
					mb: 2,
				}}>
				<svg
					width={size}
					height={size}
					viewBox={`0 0 ${size} ${size}`}
					aria-label={t('Model composition chart')}>
					{/* background ring — shows through the segment gaps */}
					<circle
						cx={cx}
						cy={cx}
						r={r}
						fill="none"
						stroke="#eef1f4"
						strokeWidth={stroke}
					/>
					{arcs.map((a, idx) => (
						<circle
							key={`${idx}-${a.category}-${a.name}`}
							cx={cx}
							cy={cx}
							r={r}
							fill="none"
							stroke={a.color}
							strokeWidth={stroke}
							strokeDasharray={a.dashArray}
							strokeDashoffset={a.dashOffset}
							// start at 12 o'clock and run clockwise
							transform={`rotate(-90 ${cx} ${cx})`}
							strokeLinecap="butt">
							<title>
								{`${a.name} · ${Math.round(a.value)}%`}
							</title>
						</circle>
					))}
				</svg>
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						pointerEvents: 'none',
					}}>
					<Typography
						sx={{
							fontSize: 26,
							fontWeight: 800,
							color: '#1b2733',
							lineHeight: 1,
						}}>
						{factorCount}
					</Typography>
					<Typography
						sx={{
							fontSize: 11.5,
							color: '#9aa2ad',
							mt: 0.25,
						}}>
						{factorCount === 1 ? t('factor') : t('factors')}
					</Typography>
				</Box>
			</Box>

			{/* One line per factor — colour matching its slice, name, and its
			    share of the overall score, so the donut and the list agree. */}
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
				{segs.map((s, idx) => {
					const pct = Math.round(s.value);
					let subtitle: string;
					if (s.unallocated) {
						subtitle = t('not yet weighted');
					} else if (s.highestOf) {
						subtitle = t('worst of {{n}} factors', {
							n: s.highestOf,
						});
					} else {
						subtitle = t('{{within}}% of {{cat}}', {
							within: s.within,
							cat: s.category,
						});
					}
					return (
						<Box
							key={`${idx}-${s.category}-${s.name}`}
							sx={{
								display: 'flex',
								alignItems: 'flex-start',
								gap: 1,
							}}>
							<Box
								sx={{
									width: 10,
									height: 10,
									mt: '3px',
									borderRadius: '3px',
									background: s.color,
									flex: 'none',
								}}
							/>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography
									sx={{
										fontSize: 13,
										fontWeight: 600,
										color: '#3a4451',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}>
									{s.name}
								</Typography>
								<Typography
									sx={{
										fontSize: 11,
										color: '#aab1bb',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}>
									{subtitle}
								</Typography>
							</Box>
							<Typography
								sx={{
									fontSize: 13,
									fontWeight: 700,
									color: '#1b2733',
									flex: 'none',
									minWidth: 34,
									textAlign: 'right',
								}}>
								{pct}%
							</Typography>
						</Box>
					);
				})}
			</Box>
		</>,
	);
};

export default ModelDonut;
